import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Apex platform hosts (exact match only — subdomains are NOT platform).
const PLATFORM_APEX = new Set([
  'localhost',
  '127.0.0.1',
  'jediacademy.com.br',
  'www.jediacademy.com.br',
  'falacosagat.com.br',
  'www.falacosagat.com.br',
]);

// Lovable infra suffixes — anything under these is platform (preview, sandbox, etc.)
const PLATFORM_SUFFIXES = ['.lovable.app', '.lovable.dev', '.lovableproject.com'];

// Wildcard parents that host academy subdomains (Phase 0 hedge).
const ACADEMY_WILDCARD_PARENTS = ['falacosagat.com.br', 'jediacademy.com.br'];

function isPlatformApex(hostname: string): boolean {
  if (PLATFORM_APEX.has(hostname)) return true;
  return PLATFORM_SUFFIXES.some((s) => hostname.endsWith(s));
}

/** Returns the academy slug if hostname matches `<slug>.<wildcard parent>`. */
function extractAcademySlug(hostname: string): string | null {
  for (const parent of ACADEMY_WILDCARD_PARENTS) {
    const suffix = `.${parent}`;
    if (hostname.endsWith(suffix) && hostname !== parent && hostname !== `www.${parent}`) {
      const slug = hostname.slice(0, -suffix.length);
      // Single-label slug only (no nested subdomains)
      if (slug && !slug.includes('.')) return slug;
    }
  }
  return null;
}

export function useCustomDomain() {
  const rawHostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const hostname = rawHostname.toLowerCase();

  // Phase 0: wildcard subdomain hedge — `<slug>.falacosagat.com.br` resolves to academies.slug.
  const wildcardSlug = extractAcademySlug(hostname);

  const isPlatform = isPlatformApex(hostname);
  const isCustom = !!hostname && !isPlatform && !wildcardSlug;
  const apex = hostname.startsWith('www.') ? hostname.slice(4) : hostname;
  const www = `www.${apex}`;
  const variants = [apex, www];

  // Branch A — wildcard subdomain (no DNS work for the gym).
  const wildcardQuery = useQuery({
    queryKey: ['custom-domain-wildcard', wildcardSlug],
    queryFn: async () => {
      const { data, error: err } = await supabase
        .from('academies')
        .select('id')
        .eq('slug', wildcardSlug!)
        .eq('is_active', true)
        .maybeSingle();
      if (err) throw err;
      return data?.id ?? null;
    },
    enabled: !!wildcardSlug,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Branch B — full custom domain (existing behavior).
  const customQuery = useQuery({
    queryKey: ['custom-domain-resolve', apex],
    queryFn: async () => {
      const { data, error: err } = await supabase
        .from('academy_landing_config')
        .select('academy_id')
        .in('custom_domain', variants)
        .limit(1)
        .maybeSingle();
      if (err) throw err;
      return data?.academy_id ?? null;
    },
    enabled: isCustom,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  if (wildcardSlug) {
    return {
      isCustomDomain: true,
      academyId: wildcardQuery.data ?? null,
      isLoading: wildcardQuery.isLoading,
      notFound: !wildcardQuery.isLoading && !wildcardQuery.data && !wildcardQuery.error,
    };
  }

  return {
    isCustomDomain: isCustom,
    academyId: isCustom ? (customQuery.data ?? null) : null,
    isLoading: isCustom ? customQuery.isLoading : false,
    notFound: isCustom && !customQuery.isLoading && !customQuery.data && !customQuery.error,
  };
}
